// Explorer: an accessible, opaque organizer over the same authorized event collection as the pond.
// Personal collections/preferences live under the current account's browser key; they never grant access.
import { api, type Lilypad, type User } from '../api';
import { h } from './panels';
import '../explorer.css';

type Layout = 'cards' | 'list';
type Sort = 'date' | 'created' | 'name';
type Theme = 'light' | 'dark' | 'system';
type Event = Lilypad & { cover_media_id?: string | null; cover_style?: string | null; can_manage?: boolean; can_contribute?: boolean };
type Collection = { id: string; name: string; events: string[] };
interface Preferences { layout: Layout; sort: Sort; theme: Theme; collections: Collection[] }
export interface ExplorerUpdate { lilypads?: Lilypad[]; loading?: boolean; error?: string | null }
export interface ExplorerOptions extends ExplorerUpdate {
  user: User;
  lilypads: Lilypad[];
  onOpen: (lilypad: Lilypad, section?: 'photos' | 'people') => void;
  onCreate: () => void;
  onAccount?: () => void;
  onReload?: () => Promise<Lilypad[] | void>;
}
export interface ExplorerElement extends HTMLElement {
  update: (state: ExplorerUpdate) => void;
  setPresented: (presented: boolean) => void;
  cleanup: () => void;
}

let sequence = 0;
const BATCH = 12;
const styles = new Set(['classic', 'blossom', 'sunny', 'evening']);
const dateLabel = (value: string) => {
  const date = new Date(value.length === 10 ? `${value}T12:00:00` : value);
  return Number.isNaN(date.getTime()) ? 'Date not set' : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};
const time = (value: string) => { const n = Date.parse(value); return Number.isFinite(n) ? n : 0; };
const button = (label: string, action: () => void, kind = '') => h('button', { type: 'button', class: `explorer-button ${kind}`, onClick: action }, label);

/** Returns a mountable organizer. update() preserves filters, search and personal preferences. */
export function createExplorer(options: ExplorerOptions): ExplorerElement {
  const prefix = `explorer-${++sequence}`;
  const key = `lilypad.explorer.v1:${options.user.id}`;
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  let preferences: Preferences = { layout: 'cards', sort: 'date', theme: 'system', collections: [] };
  let storageAvailable = true;
  let destroyed = false;
  let presented = true;
  let events = [...options.lilypads] as Event[];
  let loading = options.loading ?? false;
  let error = options.error ?? null;
  let query = '';
  let scope = 'all';
  let visible = BATCH;
  try {
    const raw: unknown = JSON.parse(localStorage.getItem(key) ?? 'null');
    if (raw && typeof raw === 'object') {
      const saved = raw as Record<string, unknown>;
      if (saved.layout === 'cards' || saved.layout === 'list') preferences.layout = saved.layout;
      if (saved.sort === 'date' || saved.sort === 'created' || saved.sort === 'name') preferences.sort = saved.sort;
      if (saved.theme === 'light' || saved.theme === 'dark' || saved.theme === 'system') preferences.theme = saved.theme;
      if (Array.isArray(saved.collections)) {
        const ids = new Set<string>();
        preferences.collections = saved.collections.slice(0, 30).flatMap((item: unknown) => {
          if (!item || typeof item !== 'object') return [];
          const c = item as Record<string, unknown>;
          if (typeof c.id !== 'string' || c.id.length > 100 || ids.has(c.id) || typeof c.name !== 'string' || !c.name.trim() || !Array.isArray(c.events)) return [];
          ids.add(c.id);
          return [{ id: c.id, name: c.name.trim().slice(0, 40), events: [...new Set(c.events.filter((id): id is string => typeof id === 'string' && id.length <= 100))].slice(0, 10000) }];
        });
      }
    }
  } catch { storageAvailable = false; }

  const root = h('section', { class: 'lp-explorer', 'aria-label': 'Your lilypads in Explorer' }) as ExplorerElement;
  const notice = h('p', { class: 'explorer-local-notice', role: 'status', 'aria-live': 'polite' });
  const nav = h('nav', { class: 'explorer-nav', 'aria-label': 'Filter lilypads' });
  const collectionNav = h('div', { class: 'explorer-collection-nav' });
  const newName = h('input', { id: `${prefix}-collection-name`, maxlength: 40, autocomplete: 'off', placeholder: 'e.g. Family trips' });
  const collectionError = h('p', { class: 'explorer-field-error', role: 'alert' });
  const collectionForm = h('form', { class: 'explorer-collection-form', hidden: true, onSubmit: (event: SubmitEvent) => {
    event.preventDefault();
    const name = newName.value.trim();
    if (!name) { collectionError.textContent = 'Give the collection a name.'; return; }
    if (preferences.collections.length >= 30) { collectionError.textContent = 'This device can keep 30 personal collections.'; return; }
    if (preferences.collections.some((c) => c.name.toLocaleLowerCase() === name.toLocaleLowerCase())) { collectionError.textContent = 'There is already a collection with that name.'; return; }
    preferences.collections.push({ id: `collection-${crypto.randomUUID()}`, name, events: [] });
    newName.value = ''; collectionError.textContent = ''; collectionForm.hidden = true;
    persist(); renderNavigation(); renderResults();
    announce(`Collection “${name}” created on this device. Choose Collections on a lilypad to add it.`);
    addCollection.focus();
  } }, h('label', { for: `${prefix}-collection-name` }, 'Collection name'), newName, collectionError,
  h('div', { class: 'explorer-inline-actions' }, h('button', { type: 'submit', class: 'explorer-button primary' }, 'Add'),
    button('Cancel', () => { collectionForm.hidden = true; collectionError.textContent = ''; addCollection.focus(); }, 'quiet')));
  const addCollection = button('New collection', () => {
    collectionForm.hidden = !collectionForm.hidden;
    if (!collectionForm.hidden) newName.focus();
  }, 'quiet explorer-new-collection');
  const collectionDetails = h('details', { class: 'explorer-collections', open: window.innerWidth > 760 },
    h('summary', {}, 'My collections'), h('p', { class: 'explorer-device-note' }, 'For this account, on this device. Collections do not change sharing.'),
    collectionNav, addCollection, collectionForm);
  const sidebar = h('aside', { class: 'explorer-sidebar' }, nav, collectionDetails,
    options.onAccount ? button('Your account', options.onAccount, 'quiet explorer-account') : null);

  const heading = h('h1', { id: `${prefix}-heading` }, 'My lilypads');
  const theme = h('select', { id: `${prefix}-theme`, onChange: () => {
    preferences.theme = theme.value as Theme; persist(); applyTheme();
  } }, h('option', { value: 'system' }, 'Device theme'), h('option', { value: 'light' }, 'Light'), h('option', { value: 'dark' }, 'Dark'));
  theme.value = preferences.theme;
  const header = h('header', { class: 'explorer-heading' },
    h('div', {}, h('p', { class: 'explorer-eyebrow' }, 'Your memories, together'), heading,
      h('p', { class: 'explorer-subtitle' }, 'Find the events you have created or joined.')),
    h('div', { class: 'explorer-heading-actions' }, h('label', { class: 'explorer-theme-label', for: `${prefix}-theme` }, 'Appearance', theme), button('Create lilypad', options.onCreate, 'primary')));
  const search = h('input', { id: `${prefix}-search`, type: 'search', placeholder: 'Search by event, host or description', autocomplete: 'off', onInput: () => {
    query = search.value; visible = BATCH; renderResults();
  } });
  const sort = h('select', { id: `${prefix}-sort`, onChange: () => {
    preferences.sort = sort.value as Sort; visible = BATCH; persist(); renderResults();
  } }, h('option', { value: 'date' }, 'Event date: newest first'), h('option', { value: 'created' }, 'Recently created'), h('option', { value: 'name' }, 'Event name: A–Z'));
  sort.value = preferences.sort;
  const cardsButton = button('Large thumbnails', () => setLayout('cards'));
  const listButton = button('List', () => setLayout('list'));
  const resultStatus = h('p', { class: 'explorer-result-count', role: 'status', 'aria-live': 'polite', 'aria-atomic': 'true' });
  const results = h('div', { class: 'explorer-results', tabindex: -1 });
  const feedback = h('div', { class: 'explorer-feedback' });
  const toolbar = h('div', { class: 'explorer-toolbar' },
    h('label', { class: 'explorer-search', for: `${prefix}-search` }, 'Search your lilypads', search),
    h('label', { class: 'explorer-sort', for: `${prefix}-sort` }, 'Sort by', sort),
    h('div', { class: 'explorer-layout-switch', role: 'group', 'aria-label': 'Event layout' }, cardsButton, listButton));
  const main = h('div', { class: 'explorer-main', 'aria-labelledby': heading.id }, header, toolbar, notice, feedback, resultStatus, results);
  root.append(sidebar, main);

  function announce(message: string): void {
    notice.textContent = message + (storageAvailable ? '' : ' Changes are available for this visit; this browser could not save them.');
  }
  function persist(): void {
    try { localStorage.setItem(key, JSON.stringify(preferences)); storageAvailable = true; }
    catch { storageAvailable = false; announce('Your changes are ready.'); }
  }
  function applyTheme(): void {
    const resolved = preferences.theme === 'system' ? (mediaQuery.matches ? 'dark' : 'light') : preferences.theme;
    root.dataset.theme = resolved;
    if (presented) document.documentElement.dataset.explorerTheme = resolved;
  }
  function setLayout(layout: Layout): void { preferences.layout = layout; persist(); renderResults(); }
  function own(event: Event): boolean { return event.owner_id === options.user.id || event.mine; }
  function canManage(event: Event): boolean { return typeof event.can_manage === 'boolean' ? event.can_manage : event.owner_id === options.user.id || options.user.tier === 'admin'; }
  function inCollection(event: Event, id: string): boolean { return preferences.collections.find((c) => c.id === id)?.events.includes(event.id) ?? false; }
  function chooseScope(next: string): void {
    scope = next; visible = BATCH;
    for (const control of root.querySelectorAll<HTMLButtonElement>('[data-explorer-scope]')) {
      const selected = control.dataset.explorerScope === scope;
      control.classList.toggle('selected', selected); control.setAttribute('aria-pressed', String(selected));
    }
    renderResults();
  }
  function scopeButton(label: string, value: string, count: number): HTMLButtonElement {
    return h('button', { type: 'button', class: `explorer-nav-button${scope === value ? ' selected' : ''}`, 'data-explorer-scope': value, 'aria-pressed': String(scope === value), onClick: () => chooseScope(value) },
      h('span', {}, label), h('span', { class: 'explorer-nav-count', 'aria-label': `${count} lilypads` }, String(count)));
  }
  function renderNavigation(): void {
    nav.replaceChildren(scopeButton('All lilypads', 'all', events.length), scopeButton('Owned by me', 'owned', events.filter(own).length), scopeButton('Shared with me', 'shared', events.filter((event) => !own(event)).length));
    collectionNav.replaceChildren(...preferences.collections.map((collection) => {
      const remove = button('Remove', () => {
        if (!window.confirm(`Remove your collection “${collection.name}” from this device? The lilypads and their photos will stay.`)) return;
        preferences.collections = preferences.collections.filter((c) => c.id !== collection.id);
        if (scope === collection.id) scope = 'all';
        persist(); renderNavigation(); renderResults(); announce(`Collection “${collection.name}” removed. Its lilypads are still in All lilypads.`);
        addCollection.focus();
      }, 'quiet explorer-remove-collection');
      remove.setAttribute('aria-label', `Remove personal collection ${collection.name}`);
      return h('div', { class: 'explorer-collection-row' }, scopeButton(collection.name, collection.id, events.filter((event) => inCollection(event, collection.id)).length), remove);
    }));
    if (!preferences.collections.length) collectionNav.append(h('p', { class: 'explorer-device-note' }, 'Create a collection to keep related events together.'));
  }
  function cover(event: Event): HTMLElement {
    const cover = h('div', { class: 'explorer-cover', 'aria-hidden': 'true' });
    cover.dataset.style = styles.has(event.cover_style ?? '') ? event.cover_style! : 'classic';
    cover.append(h('span', { class: 'explorer-cover-water' }), h('span', { class: 'explorer-cover-leaf' }), h('span', { class: 'explorer-cover-flower' }, '✿'));
    if (event.cover_media_id) {
      const photo = h('img', { src: api.thumbUrl(event.cover_media_id), alt: '', loading: 'lazy', decoding: 'async' });
      photo.addEventListener('error', () => photo.remove(), { once: true });
      cover.append(photo);
    }
    return cover;
  }
  function collectionsPicker(event: Event): HTMLElement {
    const summary = h('summary', {}, 'Collections');
    const panel = h('div', { class: 'explorer-collection-picker-panel' }, h('p', {}, 'Organize on this device. Sharing stays the same.'));
    const picker = h('details', { class: 'explorer-collection-picker' }, summary, panel);
    if (!preferences.collections.length) {
      panel.append(h('p', { class: 'explorer-device-note' }, 'Create a collection under My collections first.'));
    } else {
      for (const collection of preferences.collections) {
        const checkbox = h('input', { type: 'checkbox', checked: collection.events.includes(event.id), onChange: () => {
          collection.events = checkbox.checked ? [...new Set([...collection.events, event.id])] : collection.events.filter((id) => id !== event.id);
          persist(); renderNavigation();
          if (scope === collection.id && !checkbox.checked) {
            renderResults(); results.focus({ preventScroll: true });
            announce(`Removed from “${collection.name}”. The event is still in All lilypads.`);
          }
        } });
        panel.append(h('label', {}, checkbox, h('span', {}, collection.name)));
      }
    }
    return picker;
  }
  function eventCard(event: Event): HTMLElement {
    const mine = own(event), manage = canManage(event);
    const open = h('button', { type: 'button', class: 'explorer-event-open', onClick: () => options.onOpen(event, 'photos') },
      cover(event), h('span', { class: 'explorer-event-copy' }, h('span', { class: 'explorer-event-title' }, event.title),
        h('span', { class: 'explorer-event-date' }, dateLabel(event.event_date)),
        h('span', { class: 'explorer-event-host' }, mine ? 'Created by you' : `Hosted by ${event.owner_name}`)));
    open.setAttribute('aria-label', `Open ${event.title}, ${dateLabel(event.event_date)}`);
    const counts = h('p', { class: 'explorer-event-counts' }, `${event.media_count} ${event.media_count === 1 ? 'upload' : 'uploads'} · ${event.member_count} ${event.member_count === 1 ? 'person' : 'people'}`);
    const access = h('details', { class: 'explorer-access' }, h('summary', {}, 'Access details'),
      h('div', { class: 'explorer-access-content' },
        h('p', {}, 'Photos and videos are available to admitted members of this lilypad.'),
        h('p', {}, manage ? 'You can manage this event’s guest codes and people.' : 'The host manages invitations and guest codes.'),
        typeof event.can_contribute === 'boolean' ? h('p', {}, event.can_contribute ? 'You can add photos and videos.' : 'Adding photos and videos is currently unavailable for your account.') : null,
        button(manage ? 'People & access' : 'View people', () => options.onOpen(event, 'people'), 'quiet')));
    return h('li', { class: 'explorer-event-item' }, h('article', { class: 'explorer-event' }, open,
      h('div', { class: 'explorer-event-meta' }, counts, h('span', { class: `explorer-badge${mine ? ' owned' : ''}` }, mine ? 'Your event' : 'Shared with you')),
      h('div', { class: 'explorer-event-tools' }, access, collectionsPicker(event))));
  }
  function filteredEvents(): Event[] {
    const searchText = query.trim().toLocaleLowerCase();
    return events.filter((event) => {
      const matchesScope = scope === 'all' || (scope === 'owned' && own(event)) || (scope === 'shared' && !own(event)) || inCollection(event, scope);
      return matchesScope && (!searchText || `${event.title} ${event.owner_name} ${event.description}`.toLocaleLowerCase().includes(searchText));
    }).sort((a, b) => preferences.sort === 'name' ? a.title.localeCompare(b.title) || a.id.localeCompare(b.id)
      : (preferences.sort === 'created' ? time(b.created_at) - time(a.created_at) : time(b.event_date) - time(a.event_date)) || a.title.localeCompare(b.title) || a.id.localeCompare(b.id));
  }
  async function reload(): Promise<void> {
    if (!options.onReload || loading) return;
    loading = true; error = null; renderResults();
    try { const loaded = await options.onReload(); if (destroyed) return; if (loaded) events = [...loaded]; }
    catch (caught) { if (destroyed) return; error = caught instanceof Error ? caught.message : 'Please try again.'; }
    finally { if (!destroyed) { loading = false; renderNavigation(); renderResults(); } }
  }
  function renderResults(): void {
    root.setAttribute('aria-busy', String(loading));
    cardsButton.setAttribute('aria-pressed', String(preferences.layout === 'cards'));
    listButton.setAttribute('aria-pressed', String(preferences.layout === 'list'));
    feedback.replaceChildren();
    if (error) feedback.append(h('div', { class: 'explorer-error', role: 'alert' },
      h('div', {}, h('strong', {}, events.length ? 'Your lilypads could not be refreshed.' : 'Your lilypads could not be loaded.'), h('p', {}, error), events.length ? h('p', {}, 'The previously loaded list is shown below.') : null),
      options.onReload ? button('Try again', () => void reload(), 'outline') : null));
    if (loading && !events.length) {
      resultStatus.textContent = 'Loading your lilypads…';
      results.replaceChildren(h('div', { class: 'explorer-empty' }, h('h2', {}, 'Getting your events ready'), h('p', {}, 'Your lilypads will appear here.')));
      return;
    }
    if (!events.length && error) { resultStatus.textContent = ''; results.replaceChildren(); return; }
    if (!events.length) {
      resultStatus.textContent = 'No lilypads yet';
      results.replaceChildren(h('div', { class: 'explorer-empty' }, h('span', { class: 'explorer-empty-leaf', 'aria-hidden': 'true' }, '✿'),
        h('h2', {}, 'A place for your next get-together'), h('p', {}, 'Create a lilypad for an event, or open the invitation link someone shared with you.'), button('Create your first lilypad', options.onCreate, 'primary')));
      return;
    }
    const filtered = filteredEvents();
    resultStatus.textContent = loading ? 'Refreshing your lilypads…' : `${filtered.length} ${filtered.length === 1 ? 'lilypad' : 'lilypads'}${query.trim() ? ' matching your search' : ''}`;
    if (!filtered.length) {
      const selectedCollection = preferences.collections.find((c) => c.id === scope);
      results.replaceChildren(h('div', { class: 'explorer-empty' }, h('h2', {}, selectedCollection && !query.trim() ? 'This collection is empty' : 'No lilypads match these filters'),
        h('p', {}, selectedCollection && !query.trim() ? 'Open All lilypads, then choose Collections on an event to add it here.' : 'Try another name or show all the lilypads you have joined.'),
        button('Show all lilypads', () => { query = ''; search.value = ''; chooseScope('all'); search.focus(); }, 'outline')));
      return;
    }
    const shown = filtered.slice(0, visible);
    const list = h('ul', { class: `explorer-event-list ${preferences.layout}`, 'aria-label': 'Lilypads' }, ...shown.map(eventCard));
    const paging = h('div', { class: 'explorer-paging' }, h('p', {}, `Showing ${shown.length} of ${filtered.length}`));
    if (shown.length < filtered.length) {
      const showMore = button(`Show ${Math.min(BATCH, filtered.length - shown.length)} more`, () => {
        const previousCount = shown.length;
        visible += BATCH; renderResults();
        results.querySelectorAll<HTMLButtonElement>('.explorer-event-open')[previousCount]?.focus();
      }, 'outline');
      paging.append(showMore, button(`Show all ${filtered.length}`, () => { visible = filtered.length; renderResults(); results.focus({ preventScroll: true }); }, 'quiet'));
    }
    results.replaceChildren(list, paging);
  }

  root.update = (state) => {
    if (destroyed) return;
    if (state.lilypads) events = [...state.lilypads];
    if (state.loading !== undefined) loading = state.loading;
    if ('error' in state) error = state.error ?? null;
    renderNavigation(); renderResults();
  };
  root.setPresented = (value) => {
    if (destroyed) return;
    presented = value;
    if (presented) applyTheme();
    else delete document.documentElement.dataset.explorerTheme;
  };
  root.cleanup = () => {
    destroyed = true; mediaQuery.removeEventListener('change', applyTheme);
    if (presented) delete document.documentElement.dataset.explorerTheme;
  };
  mediaQuery.addEventListener('change', applyTheme);
  applyTheme(); renderNavigation(); renderResults();
  if (!storageAvailable) announce('Saved preferences could not be loaded.');
  return root;
}
